/* eslint-disable no-console */
import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as TypeActions from '../store/parseRfg/actionsTypes';
import * as ToastTypeActions from '../store/toast/actionsTypes';
import * as NsirTypeActions from '../store/nsir/actionsTypes';
import * as selectorsParseRfg from '../store/parseRfg/reducer';
import BaseService from '../services/BaseService';
import {
  PARSE_RFG_CONFLICT_REPLACE_DEFAULT,
  PARSE_RFG_DOCUMENTO_PATH,
  PARSE_RFG_JOB_PATH,
  PARSE_RFG_UPLOAD_FIELD,
  PARSE_RFG_UPLOAD_PATH,
  forgetParseRfgFreshUpload,
  getParseRfgApiMessage,
  isParseRfgJobOwnedByNsir,
  readParseRfgJobId,
  rememberParseRfgJobId,
} from '../helpers/auditoriaFaixa';
import {
  RELATORIO_JOB_STATUS,
  getParseRfgJobsUrl,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
  normalizeParseRfgJob,
  parseRelatorioJobsPage,
  pickParseRfgJob,
} from '../helpers/relatorioJob';

function* loadParseJobsPage(nsirId) {
  try {
    const response = yield BaseService.get({ url: getParseRfgJobsUrl({ nsirId }) });
    return parseRelatorioJobsPage(response.data).items;
  } catch (error) {
    console.log(error);
    return [];
  }
}

function* fetchJobById(jobId, nsirId) {
  if (!jobId) return null;
  try {
    const response = yield BaseService.get({ url: PARSE_RFG_JOB_PATH(jobId) });
    const raw = response.data || {};
    const ownerId = raw.nsirId || raw.entityId || null;
    if (nsirId && ownerId && ownerId !== nsirId) return null;
    if (nsirId && !ownerId) return null;
    return normalizeParseRfgJob(raw, nsirId);
  } catch (error) {
    console.log(error);
    return null;
  }
}

function* startParseRfg({ nsirId, file, force }) {
  try {
    const formData = new FormData();
    formData.append(PARSE_RFG_UPLOAD_FIELD, file);
    const response = yield BaseService.postMultipart({
      url: PARSE_RFG_UPLOAD_PATH(nsirId, { force }),
      data: formData,
    });
    const job = normalizeParseRfgJob(response.data, nsirId);
    rememberParseRfgJobId(nsirId, job.id);
    forgetParseRfgFreshUpload(nsirId);
    yield put({
      type: TypeActions.PARSE_RFG_START_SUCCESS,
      job,
      nsirId,
    });
    yield put({
      type: ToastTypeActions.TOAST_MESSAGE_SUCCESS,
      message: 'RFG enviado. O processamento ocorre em segundo plano.',
    });
  } catch (error) {
    console.log(error);
    const status = error.response && error.response.status;
    if (status === 409 && !force) {
      yield put({
        type: TypeActions.PARSE_RFG_START_CONFLICT,
        message: getParseRfgApiMessage(error, PARSE_RFG_CONFLICT_REPLACE_DEFAULT),
      });
      return;
    }
    let message = 'Erro ao enviar o RFG.';
    if (status === 403) {
      message = 'Sem permissão para enviar o RFG.';
    } else if (status === 400) {
      message = getParseRfgApiMessage(
        error,
        'Não foi possível processar o RFG agora. Só aceitamos Em Criação ou Aguardando Inspeção (neste último, confirme o alinhamento com o inspetor). Verifique também se já existe um processamento em andamento.'
      );
    } else {
      message = getParseRfgApiMessage(error, message);
    }
    yield put({ type: TypeActions.PARSE_RFG_START_ERROR });
    yield put({
      type: ToastTypeActions.TOAST_MESSAGE_ERROR,
      message,
    });
  }
}

function* fetchParseRfg({ nsirId, jobId, skipExisting }) {
  try {
    const prev = yield select(selectorsParseRfg.getParseRfgJob);
    const prevNsirId = yield select(selectorsParseRfg.getParseRfgNsirId);
    const prevForThisNsir = prev && prevNsirId === nsirId && isParseRfgJobOwnedByNsir(prev, nsirId)
      ? prev
      : null;
    const storedId = skipExisting ? null : readParseRfgJobId(nsirId);
    const resolvedId = jobId || (prevForThisNsir && prevForThisNsir.id) || storedId;
    const canFetchById = !skipExisting || (prevForThisNsir && prevForThisNsir.id === resolvedId);
    let job = canFetchById ? yield fetchJobById(resolvedId, nsirId) : null;
    if (!job && nsirId && !skipExisting) {
      const items = yield loadParseJobsPage(nsirId);
      const picked = pickParseRfgJob(items, { jobId: resolvedId, nsirId });
      job = picked && isParseRfgJobOwnedByNsir(picked, nsirId)
        ? normalizeParseRfgJob(picked, nsirId)
        : null;
    }
    if (!job) {
      yield put({
        type: TypeActions.PARSE_RFG_FETCH_SUCCESS,
        job: skipExisting ? prevForThisNsir : null,
        nsirId,
      });
      return;
    }
    if (isParseRfgJobOwnedByNsir(job, nsirId)) {
      rememberParseRfgJobId(nsirId, job.id);
    }
    yield put({
      type: TypeActions.PARSE_RFG_FETCH_SUCCESS,
      job,
      nsirId,
    });
    const sameJob = prev && prev.id === job.id;
    const wasInFlight = sameJob && isRelatorioJobInFlight(prev.status);
    if (isRelatorioJobAvailable(job.status) && nsirId) {
      if (wasInFlight) {
        yield put({ type: NsirTypeActions.NSIRS_FIND_BY_ID, id: nsirId, silent: true });
      }
      const documento = yield select(selectorsParseRfg.getParseRfgDocumento);
      if (!documento || wasInFlight) {
        yield put({ type: TypeActions.PARSE_RFG_FETCH_DOCUMENTO, jobId: job.id });
      }
    }
    if (job.status === RELATORIO_JOB_STATUS.ERROR && wasInFlight) {
      yield put({
        type: ToastTypeActions.TOAST_MESSAGE_ERROR,
        message: job.error || 'Falha ao processar o RFG. A NSIR não fica bloqueada.',
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* fetchParseRfgDocumento({ jobId }) {
  const resolvedJobId = jobId || ((yield select(selectorsParseRfg.getParseRfgJob)) || {}).id;
  if (!resolvedJobId) return;
  try {
    const response = yield BaseService.get({ url: PARSE_RFG_DOCUMENTO_PATH(resolvedJobId) });
    const data = response.data || {};
    const url = data.url || data.arquivoUrl || (data.arquivo && data.arquivo.url);
    const nome = data.nome || data.nomeArquivo || (data.arquivo && data.arquivo.nome) || 'RFG';
    if (!url) return;
    yield put({
      type: TypeActions.PARSE_RFG_FETCH_DOCUMENTO_SUCCESS,
      documento: { url, nome },
    });
  } catch (error) {
    console.log(error);
  }
}

function* watchParseRfg() {
  yield takeEvery(TypeActions.PARSE_RFG_START, startParseRfg);
  yield takeLatest(TypeActions.PARSE_RFG_FETCH, fetchParseRfg);
  yield takeLatest(TypeActions.PARSE_RFG_FETCH_DOCUMENTO, fetchParseRfgDocumento);
}

export default watchParseRfg;
